import { useEffect, useState } from 'react';
import * as asyncJobActions from '../redux/actions/asyncJobActions';
import useMongoAPI from './useMongoAPI';
import { useDispatch } from 'react-redux';

const useAsyncJob = ({
  asyncJobDialogDisabled = false,
  jobId = null,
  pollingInterval = 3000, // 3 sec
  pollingDisabled = false,
  returnResultOnComplete = false,
  onJobComplete = () => null,
  onJobFail = () => null,
} = {}) => {
  let pollTimeoutObj = null;
  const [jobIdState, setJobIdState] = useState(jobId);
  const [job, setJob] = useState({});

  const { dataAPI: asyncJobAPI } = useMongoAPI('AsyncJob');

  const dispatch = useDispatch();

  useEffect(() => {
    const asyncEffect = async () => {
      if (jobIdState) {
        await reloadJob(jobIdState);
        if (!pollingDisabled) {
          poll(jobIdState);
        }
      }
    };
    asyncEffect().catch((err) => {
      console.error(err);
    });
    return () => {
      if (pollTimeoutObj) {
        clearTimeout(pollTimeoutObj);
      }
    };
  }, []);

  useEffect(() => {
    const asyncEffect = async () => {
      if (jobIdState) {
        await reloadJob(jobIdState);
      }
    };
    asyncEffect().catch((err) => {
      console.error(err);
    });
  }, [jobIdState]);

  useEffect(() => {
    const asyncEffect = async () => {
      if (!job) {
        return;
      }
      let result = null;
      const { status } = job;
      switch (status) {
        case 'completed':
          if (returnResultOnComplete) {
            result = await fetchJobResult(job._id);
          }
          onJobComplete(job, result);
          await checkDialogJobs(); // Updates notification badge if needed
          break;
        case 'failed':
          onJobFail(job);
          await checkDialogJobs(); // Updates notification badge if needed
          break;
        default:
          break;
      }
    };
    asyncEffect().catch((err) => {
      console.error(err);
    });
  }, [job]);

  const reloadJob = async (jid) => {
    if (!jid) {
      return;
    }
    const loadedJob = await fetchJob(jobIdState);
    setJob(loadedJob);
  };

  const fetchJob = async (jid) => {
    if (!jid) {
      throw new Error('Unable to fetch status without jobId');
    }
    const { docs: jobs } = await asyncJobAPI.fetchDataById(jid, {
      fields: ['-result'],
    });
    return jobs[0];
  };

  const fetchJobResult = async (jid) => {
    if (!jid) {
      throw new Error('Unable to fetch status without jobId');
    }
    const { docs: jobs } = await asyncJobAPI.fetchDataById(jid, {
      fields: ['result'],
    });
    return jobs[0].result;
  };

  const checkDialogJobs = async () => {
    dispatch(asyncJobActions.startCheckingJobs(dispatch, { disableRefresh: true }));
  };

  const openAsyncJobsDialog = () => {
    dispatch(asyncJobActions.openJobs());
  };

  const closeAsyncJobsDialog = () => {
    dispatch(asyncJobActions.closeJobs);
  };

  const poll = (jid) => {
    pollTimeoutObj = setTimeout(async () => {
      const jobResp = await fetchJob(jid);
      setJob(jobResp);
      if (['pending', 'processing'].includes(jobResp.status)) {
        poll(jid);
      }
    }, pollingInterval);
  };

  /**
   * Use when starting a new job based on a request.
   * @param {function} asyncJobPostFunc function that is called that will return Promise<jobId>
   */
  const startJob = async (asyncJobPostFunc) => {
    const asyncJobFuncResp = await asyncJobPostFunc();
    const { jobId: retJobId } = asyncJobFuncResp || {};
    if (!retJobId) {
      console.warn(`startJob() param asyncJobPostFunc must return jobId to work.`);
      return;
    }
    setJobIdState(retJobId);
    if (!asyncJobDialogDisabled) {
      openAsyncJobsDialog();
    }
    if (!pollingDisabled) {
      poll(retJobId);
    }
    return asyncJobFuncResp;
  };

  return {
    jobId,
    job,
    startJob,
    closeAsyncJobsDialog,
    fetchJob,
    fetchJobResult,
    openAsyncJobsDialog,
  };
};

export default useAsyncJob;
